import React, { useEffect } from 'react';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import styled from '@mui/material/styles/styled';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { ReactComponent as CheckedIcon } from '../../assets/checked.svg';
import { Container, Footer } from './TwDataGrid.styles';
import {
  DataGridPro,
  DataGridProProps,
  GridRowSelectionModel,
  GridInputRowSelectionModel,
  useGridApiRef,
  GridRenderCellParams
} from '@mui/x-data-grid-pro';

export interface TwTableColumn {
  id: string;
  label: string;
  minWidth?: number | string;
  align?: 'right' | 'center' | 'left';
  format?: (value: number) => string;
  maxWidth?: number | string;
}

export interface TwTableData {
  [key: string]: undefined | any;
  handleClick?: {
    onClick: () => void;
    // it's neccessary send a  and ignore list for check if the event is allowed in the cell
    ignore: string[];
  };
}

const StyledCheckbox = styled(Checkbox)({
  fontSize: '14px',
  fontFamily: 'Poppins',
  color: '#E2E2EA',
  marginRight: 0,

  '&.Mui-checked, .MuiCheckbox-indeterminate': {
    color: '#3959df'
  },
  '&.Mui-disabled': {
    color: '#E2E2EA'
  }
});

export interface TwDataGridProps extends DataGridProProps {
  /**
   * Name of the entity represented by table
   */
  entityName?: string;
  /**
   * callback called when selection is cancelled
   */
  onCancel?: () => void;
  /**
   * Main actions available in the dialog.
   */
  actions?: JSX.Element[];
  footerClassName?: string;
  boxClassName?: string;
  /**
   * Action and prop to show Select All button on footer
   */
  showSelectAllInFooter?: boolean;
  showCancelButtonInFooter?: boolean;
  onSelectAllFooter?: () => void;

  /**
   * selectedCountOverride is used to override the selected count in the footer
   */
  selectedCountOverride?: number;

  /**
   * maxHeight is used to override the max height of the grid
   */
  maxHeight?: string;
}

export interface CustomFooterComponentProps {
  rowSelectionModel?: GridInputRowSelectionModel;
  onCancel?: () => void;
  actions?: JSX.Element[];
  entityName?: string;
  footerClassName?: string;
  showSelectAllInFooter?: boolean;
  showCancelButtonInFooter?: boolean;
  onSelectAllFooter?: () => void;
  selectedCountOverride?: number;
}

export function CustomFooterComponent({
  showCancelButtonInFooter = true,
  ...props
}: CustomFooterComponentProps): JSX.Element {
  const entityCount = props.selectedCountOverride
    ? props.selectedCountOverride
    : props.rowSelectionModel && props.rowSelectionModel;

  return (
    <Footer
      sx={{ p: 1, display: 'flex', border: '1px solid #E5E7EB', borderRadius: '8px 8px 0px 0px' }}
      className={props.footerClassName}
    >
      <Stack
        spacing={2}
        direction="row"
        width="100%"
        justifyContent="space-between"
        alignItems="center"
      >
        <Stack
          spacing={2}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ marginLeft: '6px' }}
        >
          <Stack spacing={1} direction="row" justifyContent="space-between" alignItems="center">
            <CheckedIcon />
            <Typography
              variant="body1"
              fontWeight="400"
              color="#6B7280"
              fontSize="14px"
              style={{ textAlign: 'center' }}
            >
              {`${entityCount} ${props.entityName}`}
              {entityCount && (entityCount as number) > 1 ? 's' : ''} selected
            </Typography>
          </Stack>
          {props.showSelectAllInFooter && (
            <Button
              variant="text"
              onClick={props.onSelectAllFooter && props.onSelectAllFooter}
              sx={{ textTransform: 'none' }}
            >
              <Typography
                variant="body1"
                fontWeight="500"
                color="#1F2937"
                fontSize="14px"
                style={{ textAlign: 'center' }}
              >
                Select All
              </Typography>
            </Button>
          )}
          {showCancelButtonInFooter && (
            <Button
              variant="text"
              onClick={props.onCancel && props.onCancel}
              sx={{ textTransform: 'none' }}
            >
              <Typography
                variant="body1"
                fontWeight="500"
                color="#1F2937"
                fontSize="14px"
                style={{ textAlign: 'center' }}
              >
                Cancel
              </Typography>
            </Button>
          )}
        </Stack>

        {props.actions &&
          props.actions.map((action: JSX.Element, index: number) => (
            <div key={index}>{action}</div>
          ))}
      </Stack>
    </Footer>
  );
}

export const elementRenderBasic = (params: GridRenderCellParams<JSX.Element>): React.ReactNode => {
  const { value } = params;
  return value;
};

function CustomEmptyOverlayGrid(props: { entityName?: string }): JSX.Element {
  return <></>;
}

const TwDataGrid: React.FC<TwDataGridProps> = ({
  entityName,
  rowSelectionModel,
  onCancel,
  actions,
  footerClassName,
  boxClassName,
  showSelectAllInFooter,
  onSelectAllFooter,
  selectedCountOverride,
  ...rest
}): JSX.Element => {
  const apiRef = useGridApiRef();

  const autosizeOptions = {
    includeOutliers: true,
    includeHeaders: true,
    expand: true
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (rest.rows.length > 0 && apiRef.current) {
        apiRef.current.autosizeColumns(autosizeOptions);
      }
    }, 200); // Delay to allow rendering

    return (): void => clearTimeout(timeout);
  }, [rest.columns, apiRef, rest.rows]);

  return (
    <Box sx={{ width: '100%', position: 'relative' }}>
      <Container
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 50,
          maxHeight: rest.maxHeight || '70vh'
        }}
      >
        <DataGridPro
          apiRef={apiRef}
          rowSelectionModel={rowSelectionModel}
          columnBuffer={30}
          slots={{
            footer: (): JSX.Element => (
              <CustomFooterComponent
                rowSelectionModel={rowSelectionModel}
                onCancel={onCancel}
                actions={actions}
                entityName={entityName}
                footerClassName={footerClassName}
                showSelectAllInFooter={showSelectAllInFooter}
                onSelectAllFooter={onSelectAllFooter}
                selectedCountOverride={selectedCountOverride}
              />
            ),
            noRowsOverlay: (): JSX.Element => <CustomEmptyOverlayGrid entityName={entityName} />
          }}
          disableColumnMenu={true}
          disableColumnFilter={true}
          autosizeOptions={autosizeOptions}
          autosizeOnMount
          {...rest}
        />
      </Container>
    </Box>
  );
};

export default TwDataGrid;
